import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';
import uuid from 'uuid';

import { Endpoint, ToastService } from '@philips-emr/hpms-provider-commons';
import providerContext from '@philips-emr/hpms-provider-commons/lib/context/provider-context';
import { Avatar, Button, FetchAPI, FieldAPI, Panel, PanelAPI, Stamp, StampIcon, Token } from '@philips-emr/tws-components';
import { ButtonTheme, Color, LayoutAlign, LayoutFlexDirection, LayoutGap, LayoutJustify, ToastLevel, Type } from '@philips-emr/tws-constants';
import Controller, { OnAfterFetch, OnCancel, OnClick, OnClose, OnDynamicContentRender, OnMount, OnSelectItem, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { FetchEvent, MountEvent, RenderEvent, SelectItemEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { format } from '@philips-emr/tws-core/lib/services/model-service';
import { applicationContext } from '@philips-emr/tws-foundation';
import { generateUUID, isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { DashboardAliases as Aliases, FragmentNames } from '../codegen';
import { Route } from '../route';

import BaseController from './BaseController';
import { Task } from './tasks/tasks-controller';
import { TechnicalVisit, TechnicalVisitStatus } from './technical-visit';

interface RejectAppointment {
  message: string,
}

@Controller({ fragment: FragmentNames.DASHBOARD })
class DashboardController extends BaseController {

  @Inject(Aliases.PANEL_MODAL)
  private panelModal: PanelAPI;
  @Inject(Aliases.FETCH_APPOINTMENTS)
  private fetchAppointmens: FetchAPI;
  @Inject(Aliases.PANEL_APPOINTMENTS)
  private panelAppointments: PanelAPI;
  @Inject(Aliases.PANEL_TASKS)
  private panelTasks: PanelAPI;

  @OnMount(Aliases.FETCH_DASHBOARD)
  onMountFetch(event: MountEvent): void {
    applicationContext.openModule('start');
    const currentProvider = providerContext.getSelectedProvider().nrSeqProvider;
    const api = event.target as FetchAPI;
    api.setParameters({ nrSeqProvider: currentProvider });
    api.forceFetch();
    this.context.set({ nrSeqProvider: currentProvider });
  }

  @OnMount(Aliases.FIELD_PLACE_SERVICE)
  onMountPlaceService(event: MountEvent): void {
    const api = event.target as FieldAPI;
    const { addressName } = api.getData();
    api.setVisible(addressName != null);
  }

  @OnDynamicContentRender(Aliases.BUTTOMS)
  onRenderButtonRefuse(event: RenderEvent): void {
    const { data, render } = event;
    const { ieStatus, nrSeqAppointment } = data as unknown as TechnicalVisit;

    if (ieStatus == TechnicalVisitStatus.AWAITING_APPOINTMENT) {
      render(() =>
        <Panel
          uuid={uuid()}
          className={''}
          background={''}
          border={''}
          borderRadius={LayoutGap.NONE}
          boxShadow={''}
          gridArea={''}
          height={''}
          lightTheme={false}
          margin={''}
          padding={''}
          visible={true}
          alignItems={LayoutAlign.NONE}
          justifyContent={LayoutJustify.END}
          flexDirection={LayoutFlexDirection.ROW}
        >
          <Button
            key={MessageService.translate('provider.refuse')}
            onClick={this.refuseAppointment(nrSeqAppointment)}
            theme={ButtonTheme.QUIET_EMPHASIS}
            value={MessageService.translate('provider.refuse')} />
          <Button
            key={MessageService.translate('provider.confirmation')}
            onClick={this.confirmAppointment(nrSeqAppointment)}
            theme={ButtonTheme.PRIMARY}
            value={MessageService.translate('provider.confirm')} />
        </Panel >
      );
    }
  }

  @OnDynamicContentRender(Aliases.AVATAR)
  onRenderAvatar(event: RenderEvent): void {
    const { data, render } = event;
    const { suggestedDate } = data as TechnicalVisit;
    const formmatedDate = format('date', suggestedDate, { type: 'timestamp' });
    render((): ReactElement => <Avatar
      image="/static/assets/calendar_clock_mobile.svg"
      title={MessageService.translate('provider.visit-scheduling')}
      subtitle={formmatedDate} />);
  }

  @OnClose(Aliases.MODAL_REFUSE)
  onCloseModal(): void {
    this.panelModal.setVisible(false);
  }

  @OnCancel(Aliases.FORM_MESSAGE)
  onCancelForm(): void {
    this.panelModal.setVisible(false);
  }

  @OnSubmit(Aliases.FORM_MESSAGE)
  onSubmitReject(event: SubmitEvent): void {
    const nrSeqAppointment = this.context.get('nrSeqAppointment');
    const { message } = event.record as RejectAppointment;
    HttpService.post(Endpoint.POST.REJECT_APPOINTMENT, { nrSeqAppointment, message }).then(() => {
      this.fetchAppointmens.forceFetch();
      ToastService.open({ message: MessageService.translate('provider.reject-appointment-success-message'), level: ToastLevel.SUCCESS });
      this.panelModal.setVisible(false);
    }).catch((error) => {
      ToastService.open(error);
    });
  }

  @OnAfterFetch(Aliases.FETCH_APPOINTMENTS)
  OnAfterFetch(event: FetchEvent): void {
    const data = event.data;
    if (isEmptyArrayOrObject(data)) {
      this.panelAppointments.setVisible(false);
    }
  }

  @OnAfterFetch(Aliases.FETCH_TASKS)
  OnAfterFetchTasks(event: FetchEvent): void {
    const data = event.data;
    if (isEmptyArrayOrObject(data)) {
      this.panelTasks.setVisible(false);
    }
  }

  @OnSelectItem(Aliases.DECK_TASKS)
  onClickCardVisit(event: SelectItemEvent<Task>): void {
    const { nrSequenceTask } = event.item;
    applicationContext.openModule('tasks', { preventNavigation: true });
    this.historyService.push(Route.TASK_DETAILS, { nrSequenceTask });
  }

  @OnClick(Aliases.BUTTON_SEE_MORE)
  onClickSeeMore(): void {
    applicationContext.openModule('tasks');
  }

  @OnDynamicContentRender(Aliases.CARD_TASK)
  onRenderCardTask(event: RenderEvent): void {
    if (event.data) {
      const { data, render } = event;
      const { delayed } = data as Task;
      if (delayed == 'S') {
        const token = (): ReactElement =>
          <Panel className="" uuid={generateUUID()} margin="0 0 0 1.5rem" justifyContent={LayoutJustify.CENTER} alignItems={LayoutAlign.CENTER}>
            <Token value={MessageService.translate('provider.overdue')} type={Type.DANGER} />
          </Panel>;
        render(token);

      }
    }
  }

  @OnDynamicContentRender(Aliases.FIELD_ALERT)
  onRenderAlertDelayed(event: RenderEvent): void {
    if (event.data) {
      const { data, render } = event;
      const { delayed } = data as Task;
      if (delayed == 'S') {
        const token = (): ReactElement =>
          <Panel className="" uuid={generateUUID()} justifyContent={LayoutJustify.INITIAL} alignItems={LayoutAlign.END}>
            <Stamp color={Color.SAFETY_ORANGE} icon={StampIcon.WARNING} label="" />
          </Panel>;
        render(token);

      }
    }
  }

  private refuseAppointment(nrSeqAppointment: number): () => void {
    return () => {
      this.panelModal.setVisible(true);
      this.context.set({ nrSeqAppointment });
    };
  }

  private confirmAppointment(nrSeqAppointment: number): () => void {
    return () => {
      HttpService.post(Endpoint.POST.CONFIRM_APPOINTMENT, { nrSeqAppointment }).then(() => {
        this.fetchAppointmens.forceFetch();
        ToastService.open({ message: MessageService.translate('provider.confirm-appointment-sucess-message'), level: ToastLevel.SUCCESS });
      }).catch((error) => {
        ToastService.open(error);
      });
    };
  }

  public pushRouteByOpenModule(moduleId: string, route: string, params: Record<string, number>): void {

    applicationContext.openModule(moduleId, {
      at: {
        params: params,
        path: route,
      },
    });

    applicationContext.pushNavigationItem(() => {
      applicationContext.openModule('start');
    });
  }
}

export default DashboardController;
